import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaSearch, FaPlus, FaList, FaChartBar, FaEye, FaCheck, FaTimes } from 'react-icons/fa';
import { Modal, Row, Col, Form, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../api/apiClient';
import { toast } from 'react-toastify';

const SurveyList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [courses, setCourses] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    survey_type: 'organization',
    organization_id: user?.role === 2 ? user.organization_id : '',
    course_id: '',
    image: null,
    status: 1
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchSurveys();
    fetchOrganizations();
    fetchCourses();
  }, []);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      const response = await api.surveys.getAll({ page: 1, limit: 500 });

      let surveysData = [];
      if (response?.data?.data && Array.isArray(response.data.data)) {
        surveysData = response.data.data;
      } else if (response?.data && Array.isArray(response.data)) {
        surveysData = response.data;
      }

      setSurveys(surveysData);
    } catch (error) {
      console.error('Error fetching surveys:', error);
      toast.error('Failed to load surveys: ' + (error.response?.data?.message || error.message));
      setSurveys([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await api.organizations.getAll();
      setOrganizations(response.data.data || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setOrganizations([]);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await api.courses.getAll(true);
      setCourses(response.data.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCourses([]);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterTypeChange = (event) => {
    setFilterType(event.target.value);
  };

  const handleFilterStatusChange = (event) => {
    setFilterStatus(event.target.value);
  };

  const handleDeleteClick = (survey) => {
    setSurveyToDelete(survey);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!surveyToDelete) return;
    
    setDeleteLoading(surveyToDelete.id);
    
    try {
      await api.surveys.delete(surveyToDelete.id);
      toast.success('Survey deleted successfully!');
      setShowDeleteModal(false);
      setSurveyToDelete(null);
      fetchSurveys();
    } catch (error) {
      console.error('Error deleting survey:', error);
      toast.error('Failed to delete survey: ' + (error.response?.data?.message || error.message));
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSurveyToDelete(null);
  };

  const handleShowModal = async (survey = null) => {
    if (survey) {
      setEditingSurvey(survey);
      setFormData({
        title: survey.title || '',
        description: survey.description || '',
        survey_type: survey.survey_type || 'organization',
        organization_id: survey.organization_id || '',
        course_id: survey.course_id || '',
        image: null,
        status: survey.status
      });
      if (survey.image_path) {
        setImagePreview(`${process.env.REACT_APP_API_BASE_URL?.replace('/api', '')}${survey.image_path}`);
      }
    } else {
      setEditingSurvey(null);
      setFormData({
        title: '',
        description: '',
        survey_type: 'organization',
        organization_id: user?.role === 2 ? user.organization_id : '',
        course_id: '',
        image: null,
        status: 1
      });
      setImagePreview(null);
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSurvey(null);
    setFormData({
      title: '',
      description: '',
      survey_type: 'organization',
      organization_id: user?.role === 2 ? user.organization_id : '',
      course_id: '',
      image: null,
      status: 1
    });
    setImagePreview(null);
    setFormErrors({});
  };

  const handleInputChange = (event) => {
    const { name, value, type, files } = event.target;
    
    if (type === 'file') {
      const file = files[0];
      if (file) {
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
          toast.error('Please select a valid image file (JPEG, PNG, GIF, WEBP)');
          return;
        }

        if (file.size > 5 * 1024 * 1024) {
          toast.error('Image size must be less than 5MB');
          return;
        }

        setFormData(prev => ({ ...prev, [name]: file }));
        
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      
      if (name === 'survey_type' && value === 'global') {
        setFormData(prev => ({ ...prev, organization_id: '' }));
      }
    }
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.title.trim()) {
      errors.title = 'Survey title is required';
    }
    
    if (!formData.survey_type) {
      errors.survey_type = 'Survey type is required';
    }
    
    if (formData.survey_type === 'organization' && !formData.organization_id) {
      errors.organization_id = 'Organization is required for organization-level surveys';
    }
    
    if (user?.role === 2) {
      if (formData.survey_type === 'global') {
        errors.survey_type = 'You can only create organization-level surveys';
      }
      if (formData.organization_id && formData.organization_id !== user.organization_id.toString()) {
        errors.organization_id = 'You can only create surveys for your organization';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setModalLoading(true);
    
    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('description', formData.description);
      submitData.append('survey_type', formData.survey_type);
      
      if (formData.survey_type === 'organization') {
        submitData.append('organization_id', formData.organization_id);
      }
      
      if (formData.course_id) {
        submitData.append('course_id', formData.course_id);
      }
      
      if (formData.image) {
        submitData.append('image', formData.image);
      }
      
      submitData.append('status', formData.status);

      if (editingSurvey) {
        await api.surveys.update(editingSurvey.id, submitData);
        toast.success('Survey updated successfully!');
      } else {
        await api.surveys.create(submitData);
        toast.success('Survey created successfully!');
      }

      handleCloseModal();
      fetchSurveys();
    } catch (error) {
      console.error('Error saving survey:', error);
      toast.error('Failed to save survey: ' + (error.response?.data?.message || error.message));
    } finally {
      setModalLoading(false);
    }
  };

  const filteredSurveys = Array.isArray(surveys) ? surveys.filter(survey => {
    if (!survey || typeof survey !== 'object') return false;
    
    const surveyTitle = survey.title || '';
    const surveyDescription = survey.description || '';
    const searchTermLower = (searchTerm || '').toLowerCase();
    
    const matchesSearch = surveyTitle.toLowerCase().includes(searchTermLower) ||
                         surveyDescription.toLowerCase().includes(searchTermLower);
    
    const matchesType = filterType === 'all' || survey.survey_type === filterType;
    
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && survey.status === 1 && !survey.deleted) ||
                         (filterStatus === 'inactive' && survey.status === 0 && !survey.deleted) ||
                         (filterStatus === 'deleted' && survey.deleted);
    
    return matchesSearch && matchesType && matchesStatus;
  }) : [];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96 p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">

      {/* Header */}
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Manage Surveys
        </h5>
      </div>

      {/* Search + Filters + Add */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
        <div className="md:col-span-1">
          <div className="flex shadow-sm rounded-lg sm:rounded-xl overflow-hidden bg-white">
            <span className="inline-flex items-center px-3 text-purple-600 bg-white">
              <FaSearch className="w-4 h-4" />
            </span>
            <input
              type="text"
              placeholder="Search by title or description..."
              value={searchTerm}
              onChange={handleSearch}
              className="border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base form-control"
            />
          </div>
        </div>

        <div>
          <select
            value={filterType}
            onChange={handleFilterTypeChange}
            className="rounded-lg  sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select"
          >
            <option value="all">All Types</option>
            <option value="global">Global</option>
            <option value="organization">Organization</option>
          </select>
        </div>

        <div>
          <select
            value={filterStatus}
            onChange={handleFilterStatusChange}
            className="rounded-lg  sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="deleted">Deleted</option>
          </select>
        </div>

        <div>
          <button
          bsPrefix="custom-hamburger"
            onClick={() => handleShowModal()}
            className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 "
          >
            <FaPlus className="w-4 h-4" />
            Add Survey
          </button>
        </div>
      </div>

      {/* No Data */}
      {filteredSurveys.length === 0 ? (
        <div className="text-center py-10 bg-white/80 backdrop-blur rounded-xl shadow-md border border-purple-100">
          <p className="text-purple-700 font-medium text-sm sm:text-base">
            {searchTerm || filterType !== 'all' || filterStatus !== 'all'
              ? 'No surveys found matching your criteria.'
              : 'No surveys yet. Click "Add Survey" to create one.'}
          </p>
        </div>
      ) : (
        /* Table */
        <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
          <table className="m-0 min-w-max text-xs sm:text-sm table table-bordered table-hover">
            <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <tr>
                <th className="py-2 px-3 text-left">Survey Title</th>
                <th className="py-2 px-3 text-left">Type</th>
                <th className="py-2 px-3 text-left">Organization</th>
                <th className="py-2 px-3 text-left">Questions</th>
                <th className="py-2 px-3 text-left">Status</th>
                <th className="py-2 px-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSurveys.map((survey) => (
                <tr key={survey.id} className="border-b border-purple-100 hover:bg-purple-50/50 transition-all">
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-3">
                      {survey.image_path && (
                        <img
                          src={`${process.env.REACT_APP_API_BASE_URL?.replace('/api', '')}${survey.image_path}`}
                          alt={survey.title}
                          className="w-10 h-10 rounded-lg object-cover shadow-sm"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                      <div>
                        <div className="font-semibold text-gray-800">{survey.title}</div>
                        {survey.description && (
                          <div className="text-xs text-gray-600 mt-1">
                            {survey.description.length > 60 
                              ? `${survey.description.substring(0, 60)}...` 
                              : survey.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${survey.survey_type === 'global' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                      {survey.survey_type === 'global' ? 'Global' : 'Organization'}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-gray-700">
                    {survey.organization ? survey.organization.name : <span className="text-gray-400 italic">—</span>}
                  </td>
                  <td className="py-2 px-3 text-left font-medium text-gray-800">
                    {survey.question_count || 0}
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${survey.deleted ? 'bg-red-100 text-red-800' : survey.status === 1 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                      {survey.deleted ? (
                        <>Deleted</>
                      ) : survey.status === 1 ? (
                        <>Active</>
                      ) : (
                        <>Inactive</>
                      )}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex justify-left gap-2">
                      {!survey.deleted ? (
                        <>
                          <button
                            onClick={() => handleShowModal(survey)}
                            title="Edit Survey"
                            className="p-1 border-0 border-purple-300 text-purple-600 hover:bg-purple-50"
                          >
                            <FaEdit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(survey)}
                            title="Delete Survey"
                            disabled={deleteLoading === survey.id}
                            className="p-1 border-0 border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
                          >
                            {deleteLoading === survey.id ? <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div> : <FaTrash className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => navigate(`/admin/surveys/${survey.id}/questions`)}
                            title="Manage Questions"
                            className="p-1 border-0 border border-blue-300 text-blue-600 hover:bg-blue-50"
                          >
                            <FaList className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/admin/surveys/${survey.id}/responses`)}
                            title="View Responses"
                            className="p-1 border-0 border border-cyan-300 text-cyan-600 hover:bg-cyan-50"
                          >
                            <FaChartBar className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => {/* TODO: Implement restore */}}
                          title="Restore Survey"
                          className="p-2 rounded-lg border border-green-300 text-green-600 hover:bg-green-50"
                        >
                          <FaCheck className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal (match ForumList.jsx structure) */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        fullscreen="sm-down"
        backdrop="static"
      >
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:py-4 sm:px-4">
          <Modal.Title className="font-bold text-base sm:text-lg">
            {editingSurvey ? 'Edit Survey' : 'Add New Survey'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-0 sm:p-5 bg-gray-50">
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3 g-2 sm:g-3">
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Survey Title <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    isInvalid={!!formErrors.title}
                    placeholder="Enter survey title"
                    className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                  />
                  <Form.Control.Feedback type="invalid">{formErrors.title}</Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter survey description (optional)"
                    className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3 g-2 sm:g-3">
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Survey Type <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="survey_type"
                    value={formData.survey_type}
                    onChange={handleInputChange}
                    disabled={user?.role === 2}
                    className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                  >
                    <option value="organization">Organization Level</option>
                    {user?.role === 0 && <option value="global">Global (All Users)</option>}
                  </Form.Select>
                  {formErrors.survey_type && <div className="text-red-500 text-xs mt-1">{formErrors.survey_type}</div>}
                </Form.Group>
              </Col>

              {formData.survey_type === 'organization' && (
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Organization <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="organization_id"
                      value={formData.organization_id}
                      onChange={handleInputChange}
                      disabled={user?.role === 2}
                      className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                    >
                      <option value="">Select Organization</option>
                      {organizations
                        .filter(org => !org.deleted && org.status === 1)
                        .map(org => (
                          <option key={org.id} value={org.id}>{org.name}</option>
                        ))}
                    </Form.Select>
                    {formErrors.organization_id && <div className="text-red-500 text-xs mt-1">{formErrors.organization_id}</div>}
                  </Form.Group>
                </Col>
              )}
            </Row>

            <Row className="mb-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Link to Course (Optional)</Form.Label>
                  <Form.Select
                    name="course_id"
                    value={formData.course_id}
                    onChange={handleInputChange}
                    className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
                  >
                    <option value="">No Course</option>
                    {courses
                      .filter(course => !course.deleted && course.status === 1)
                      .map(course => (
                        <option key={course.id} value={course.id}>{course.name}</option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">Survey Image</Form.Label>
                  <Form.Control
                    type="file"
                    name="image"
                    onChange={handleInputChange}
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    className="rounded-lg border-purple-200 text-sm"
                  />
                  <Form.Text className="text-xs text-gray-500">Max 5MB • JPEG, PNG, GIF, WEBP</Form.Text>
                  {imagePreview && (
                    <div className="mt-3">
                      <img src={imagePreview} alt="Preview" className="max-w-xs max-h-48 rounded-lg shadow-md" />
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer className="bg-gray-100 border-0 p-3 sm:p-4">
          <Row className="w-100 g-2">
            <Col xs={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleCloseModal}
                disabled={modalLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col xs={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleSubmit}
                disabled={modalLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {modalLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {editingSurvey ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingSurvey ? 'Update Survey' : 'Create Survey'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-10 bg-gray-50">
          <div className="mb-4">
            <FaTrash size={60} className="text-red-500 mx-auto" />
          </div>
          <h5 className="font-bold text-gray-800 mb-3">Delete Survey</h5>
          <p className="text-gray-600">
            Are you sure you want to delete{' '}
            <strong>{surveyToDelete?.title}</strong>?
          </p>
          <p className="text-sm text-gray-500 mt-3">This will also delete all questions and responses permanently. This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button 
                bsPrefix="custom-hamburger"
                onClick={handleDeleteCancel}
                disabled={deleteLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button 
                bsPrefix="custom-hamburger"
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {deleteLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Survey'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SurveyList;